/**
 * @bos/portal3 — TAKE YOUR APP: the section where a finished build stops being a report
 * and becomes a file on someone's machine.
 *
 * Everything above this on the workbench answers "what did the machine do?". This answers
 * the question that comes after it, and the one the product could not previously answer at
 * all: "so where is it?". The archive is a plain same-origin `<a href>` — the documents
 * download's exact shape — so the browser attaches the session cookie itself and no token
 * ever rides a URL.
 *
 * WORDING LAWS:
 *   - THE VERDICT IS THE ONLY AUTHORITY, and this screen is downstream of it. A build the
 *     machine refused to call done is still the owner's build and still downloadable — but
 *     the download must never read like a pass. The not-ok wording leads with the refusal
 *     and points at the verdict; it does not soften it and it does not withhold the file.
 *   - A RUN WITH NO APP SAYS SO, PLAINLY. There is no disabled button, no "coming soon",
 *     and above all no link that would hand someone a broken archive. Not-yet (the run is
 *     still going) and never (the run ended without one) are DIFFERENT sentences, because
 *     they are different facts.
 *   - NOTHING IS INVENTED. The file count and the size are what the server measured on
 *     disk. The commands are the app's own `package.json` scripts, quoted. The Node
 *     version is derived from declared `engines` ranges and says where it came from. A
 *     fact the bytes do not carry is absent from the screen, not guessed at.
 *   - WHAT IS MISSING IS SAID BEFORE IT BITES: `node_modules` is not in the archive and
 *     the reason is on screen, next to the install command, not buried in a file.
 */
import type { AppPackageRead, AppPackageVerdict } from '@bos/portal3-server';
import { appPackageUrl, type StageReviewStatus } from './hooks';

/** Bytes as a person reads them. Only ever fed a number the server measured. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** The headline over a ready archive — the verdict's word, never softer than the verdict. */
export function readyHeadline(appName: string | null, verdict: AppPackageVerdict): string {
  const name = appName ?? 'Your app';
  if (verdict === 'not-ok') return `${name} is here to take — and the machine refused to call this build done.`;
  return `${name} is here. Take it.`;
}

/** The sentence under that headline. One per verdict state; none of them upgrades it. */
export function readySubline(verdict: AppPackageVerdict): string {
  switch (verdict) {
    case 'ok':
      return (
        'The machine’s final verdict on this build was OK. That is exactly as strong as what it ' +
        'checked — open The verdict for the list of what it could not prove.'
      );
    case 'not-ok':
      return (
        'The final verdict was not OK. This is still your app and these are still your bytes — ' +
        'you asked for it and the machine built it. But nothing about this download says it ' +
        'passed, because it did not. Open The verdict and read which parts fell ' +
        'short before you run this anywhere that matters.'
      );
    case 'unintelligible':
      return (
        'This run wrote a final verdict the portal cannot read, so nothing is claimed about this ' +
        'build here. What is in the archive is what was on disk.'
      );
    case 'absent':
    default:
      return (
        'This run never reached a final verdict, so nothing is claimed about this build — not ' +
        'that it passed, not that it failed. This is what the machine had written when the run ' +
        'ended.'
      );
  }
}

/** The absent sentence: not-yet vs ended-without (VerdictReview's distinction, kept). */
export function absentSentence(terminal: boolean): string {
  return terminal
    ? 'This run ended without producing an app, so there is nothing to download. Nothing is ' +
        'half-written and nothing is being held back — the machine never got that far. The ' +
        'Journal is where its story stops.'
    : 'Your app hasn’t been written yet. It is written near the very end of a build — after the ' +
        'parts are filled and the screens are made — and this turns into a download the moment it ' +
        'exists.';
}

function NodeLine({ read }: { read: AppPackageRead }) {
  const req = read.nodeRequirement;
  if (req === null) {
    return (
      <li className="dl-fact">
        <span className="dl-fact__key">Node</span>
        <span className="dl-fact__val">
          This app names no Node version anywhere in its own files, so none is claimed here.
        </span>
      </li>
    );
  }
  return (
    <li className="dl-fact">
      <span className="dl-fact__key">Node</span>
      <span className="dl-fact__val">
        {req.major} or newer
        <span className="dl-fact__why">
          {req.source === 'engines'
            ? ` — the app asks for ${req.declared ?? ''} in its own package.json.`
            : ` — this app sets no engines field, so this is the strictest floor its own dependencies declare${
                req.setBy.length > 0 ? ` (${req.setBy.slice(0, 3).join(', ')})` : ''
              }.`}
        </span>
      </span>
    </li>
  );
}

function ReadyBody({ read, runId }: { read: AppPackageRead; runId: string }) {
  return (
    <>
      <p className={`dl-headline dl-headline--${read.verdict === 'not-ok' ? 'not-ok' : 'ok'}`}>
        {readyHeadline(read.appName, read.verdict)}
      </p>
      <p className="sr__lede">{readySubline(read.verdict)}</p>

      <div className="dl-action">
        <a className="btn btn--primary" href={appPackageUrl(runId)} download>
          Download {read.filename}
        </a>
        <span className="dl-action__meta">
          {read.fileCount.toLocaleString()} file{read.fileCount === 1 ? '' : 's'} ·{' '}
          {formatBytes(read.totalBytes)} before compression
        </span>
      </div>

      <ul className="dl-facts">
        {read.installCommand !== null && (
          <li className="dl-fact">
            <span className="dl-fact__key">Install</span>
            <span className="dl-fact__val">
              <code>{read.installCommand}</code>
              {read.installCommand === 'npm ci' && (
                <span className="dl-fact__why">
                  {' '}
                  — the archive carries the app’s package-lock.json, so you get the exact
                  dependency versions the build was checked against.
                </span>
              )}
            </span>
          </li>
        )}
        {read.startCommand !== null ? (
          <li className="dl-fact">
            <span className="dl-fact__key">Start</span>
            <span className="dl-fact__val">
              <code>{read.startCommand}</code>
              {read.startScript !== null && (
                <span className="dl-fact__why"> — the app’s own start script, {read.startScript}.</span>
              )}
            </span>
          </li>
        ) : (
          <li className="dl-fact">
            <span className="dl-fact__key">Start</span>
            <span className="dl-fact__val">
              This app’s package.json declares no start script, so there is no start command to
              give you.
            </span>
          </li>
        )}
        <NodeLine read={read} />
      </ul>

      <div className="dl-caveat">
        <h3 className="dl-caveat__title">What is not in the archive, and why</h3>
        <ul className="sr-list">
          <li className="sr-list__row">
            <span className="sr-list__what">The installed dependencies</span>
            <span className="sr-list__reason">
              The machine’s own <code>node_modules</code> holds binaries compiled for the machine
              that built your app; they would not run on yours. Run{' '}
              <code>{read.installCommand ?? 'npm install'}</code> in the extracted folder and npm
              fetches the right ones for your platform.
            </span>
          </li>
          <li className="sr-list__row">
            <span className="sr-list__what">A database</span>
            <span className="sr-list__reason">
              The one in the build folder held nothing but the certification run’s own test rows,
              so shipping it would have handed you a database full of junk. Your app makes a fresh,
              empty one the first time it starts.
            </span>
          </li>
          <li className="sr-list__row">
            <span className="sr-list__what">The machine’s grading reports</span>
            <span className="sr-list__reason">
              Those are what The verdict is made of, and that is where they are read. An archive is
              not the place to argue about its own quality.
            </span>
          </li>
        </ul>
        {read.installScriptPackages.length > 0 && (
          <p className="dl-caveat__note">
            Installing runs a build step for{' '}
            {read.installScriptPackages.map((p, i) => (
              <span key={p}>
                {i > 0 && ', '}
                <code>{p}</code>
              </span>
            ))}
            . These fetch a prebuilt binary where one exists and compile from source where it does
            not — so on an unusual platform the install may ask for a C/C++ toolchain. If{' '}
            <code>{read.installCommand ?? 'npm install'}</code> fails while building one of them,
            that is what it wants.
          </p>
        )}
      </div>

      <p className="sr__meta">
        Open <code>HOW-TO-RUN.md</code> first — it is inside the archive and it was written for
        this app specifically, from its own files.
      </p>
    </>
  );
}

export interface AppDownloadProps {
  readonly runId: string;
  readonly payload: AppPackageRead | null;
  readonly status: StageReviewStatus;
  readonly error: string | null;
  /** Is the run over? Picks the honest absent sentence: not-yet vs ended-without. */
  readonly terminal: boolean;
}

export function AppDownload({ runId, payload, status, error, terminal }: AppDownloadProps) {
  return (
    <section className="sr dl" aria-label="Take your app">
      <div className="sr__head">
        <h2 className="sr__title">Take your app</h2>
      </div>

      {status === 'loading' && <p className="page__note">Checking what there is to hand over…</p>}
      {status === 'error' && (
        <p className="form__error" role="alert">
          {error ?? 'Something went wrong checking for your app.'}
        </p>
      )}

      {status === 'ready' && payload !== null && payload.state === 'absent' && (
        <p className="page__note">{absentSentence(terminal)}</p>
      )}

      {status === 'ready' && payload !== null && payload.state === 'incomplete' && (
        <>
          <p className="dl-headline dl-headline--not-ok">
            There is an app folder here, but not an app that would install.
          </p>
          <p className="sr__lede">{payload.incompleteReason}</p>
          <p className="page__note">
            Rather than hand you an archive that fails hours from now, this offers you nothing.
            Resuming the run picks up from its last checkpoint.
          </p>
        </>
      )}

      {status === 'ready' && payload !== null && payload.state === 'ready' && (
        <ReadyBody read={payload} runId={runId} />
      )}
    </section>
  );
}
